import Vue from 'vue'
import store, {Mutations, Actions} from '../store'
// Firebase App (the core Firebase SDK) is always required and must be listed first
import firebase from 'firebase'
// Add the Firebase products that you want to use
import 'firebase/auth'
import 'firebase/firestore'
import 'firebase/functions'
import 'firebase/storage'
import * as Sentry from '@sentry/vue'
import { User as SentryUser, Severity} from '@sentry/browser'
import { initServerlessFirestore, CompanyTypeEnum, UserCollection } from '@/firestore'

import { CompanyCollection, Company } from '@/firestore'


initFirebase().then(initFirebaseAuth)

async function initFirebase() {
  Vue.prototype.$firebase =
    new Promise<firebase.app.App>(async (resolve, reject) => {
      const config = await ( await fetch('/__/firebase/init.json') ).json()
      const app = await firebase.initializeApp(config)

      Vue.prototype.$firebaseConfig = config

      if (location.hostname === 'localhost') {
        if (process.env.VUE_APP_FIREBASE_EMULATORS_FIRESTORE || process.env.VUE_APP_FIREBASE_EMULATORS) {
          app.firestore().settings({ host: 'localhost:8080', ssl: false })
        }

        if (process.env.VUE_APP_FIREBASE_EMULATORS_FUNCTIONS || process.env.VUE_APP_FIREBASE_EMULATORS) {
          app.functions().useFunctionsEmulator('http://localhost:5001')
        }
      }

      initServerlessFirestore(app.firestore())
      resolve(app)
    })

  return Vue.prototype.$firebase
}

function initFirebaseAuth(firebaseApp: firebase.app.App) {
  const auth = firebase.auth()
  const sentryFirebaseScope = new Sentry.Scope()
  sentryFirebaseScope.setLevel(Severity.Debug)

  auth.onIdTokenChanged(async (user) => {
    if (!user) {
      Sentry.captureMessage('Firebase Token Changed: token removed', sentryFirebaseScope)
    }
    const idToken = user ? (await user.getIdTokenResult()).token : null
    store.commit(Mutations.UPDATE_ID_TOKEN, { idToken})
  })

  auth.onAuthStateChanged(async (user) => {
    const companyCollection = new CompanyCollection(firebaseApp.firestore())
    const idTokenResult = user ? ( await user.getIdTokenResult() ) : null
    const userClaim = idTokenResult ? idTokenResult.claims : null
    const idToken = idTokenResult ? idTokenResult.token : null

    if (user) {
      const sentryUser: SentryUser = {
        id: user.uid,
        email: user.email || undefined,
        phoneNumber: user.phoneNumber || undefined
      }
      if (userClaim && userClaim.name) {
        sentryUser.name = userClaim.name
      }

      const userCollection = new UserCollection(firebaseApp.firestore())
      const userFromDb = await userCollection.getUser(user.uid)

      if (userFromDb && userFromDb.admin && userClaim) {
        userClaim.admin = userFromDb.admin
      }

      Sentry.setUser(sentryUser)
    } else {
      Sentry.captureMessage('Firebase Auth State Changed: auth removed', sentryFirebaseScope)
    }

    let companies = user
      ? (await companyCollection.listCompaniesByUser(user.uid))
        .reduce((acc, cur) => ({
          ...acc,
          [cur.id]: cur
        }), {} as {[companyId: string]: Company})
      : null

    if (companies) {
      const adminCompanies: Company[] = Object.values(companies)
        .filter((company) => company.type === CompanyTypeEnum.ADMIN)
      for (const company of adminCompanies) {
        const restarants = await companyCollection.listCompaniesByAdminCompany(company.id)
        restarants.forEach((restaurant) => {
          if (companies) {
            companies[restaurant.id] = restaurant
          }
        })
      }
    }

    if (
      userClaim
      && userClaim.admin
    ) {
      const storeString = localStorage.getItem('store')
      if (storeString) {
        const storeData = JSON.parse(storeString)

        if (storeData && storeData.companies) {
          if (!companies) {
            companies = {}
          }

          for (const company of Object.values(storeData.companies) as Company[]) {
            if (!companies[company.id]) {
              companies[company.id] = company
            }
          }
        }
      }
    }

    store.commit(Mutations.UPDATE_USER, { user })
    store.commit(Mutations.UPDATE_USER_CLAIM, { userClaim })
    store.commit(Mutations.UPDATE_ID_TOKEN, { idToken })
    store.commit(Mutations.UPDATE_COMPANIES, { companies })
    store.dispatch(Actions.SET_USER_COMPANY)
    store.dispatch(Actions.SET_LOGIN, !!user)
  })
}


declare module 'vue/types/vue' {
  interface Vue {
    $firebase: Promise<firebase.app.App>
    $firebaseConfig: {
      projectId: string
      databaseURL: string
      storageBucket: string
      locationId: string
      apiKey: string
      authDomain: string
      messagingSenderId: string
    }
  }
}
